import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "../components/Header";
import { ChevronLeft, ArrowRight, ArrowLeft, Copy, Save, Check, RotateCcw, Zap, BookOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `/api`;

const FORMULES = [
  { id: "pour-le-moment", label: "Pour le moment", help: "Utile pour un état émotionnel, une période difficile ou une sensation de blocage." },
  { id: "pour-linstant", label: "Pour l'instant", help: "Plus doux, plus naturel, utile quand la situation peut évoluer." },
  { id: "pas-encore", label: "Pas encore", help: "Très utile pour les apprentissages, les blocages et les phrases du type \"je n'y arrive pas\"." },
  { id: "jusquici", label: "Jusqu'ici", help: "Utile pour montrer qu'un ancien fonctionnement n'est pas une fatalité." },
  { id: "actuellement", label: "Actuellement", help: "Plus neutre, plus factuel, utile pour décrire une situation sans dramatiser." }
];

const FIGE_CHOICES = [
  "Mon identité", "Ma valeur", "Mon avenir", "Mes capacités",
  "Mon corps", "Mon caractère", "Ma relation aux autres", "Mon problème",
  "Je ne sais pas", "Autre"
];

const PORTES = [
  "Demander de l'aide", "Essayer autrement", "Me reposer", "Réparer",
  "Recommencer plus petit", "Changer de méthode", "Attendre que l'émotion redescende",
  "Poser une limite", "Me former", "En parler à quelqu'un",
  "Faire un premier petit pas", "Autre"
];

const EXEMPLES = [
  { figee: "Je suis nulle.", defigee: "Je me sens nulle, pour le moment." },
  { figee: "Je suis dépressive.", defigee: "Je souffre de dépression, pour le moment." },
  { figee: "Je suis anxieuse.", defigee: "L'anxiété prend beaucoup de place, pour l'instant." },
  { figee: "Je suis incapable.", defigee: "Je n'ai pas trouvé comment faire, pas encore." },
  { figee: "Je suis foutue.", defigee: "Je traverse une période très difficile, pour le moment." },
  { figee: "Je suis un échec.", defigee: "Je traverse un échec, pour le moment." },
  { figee: "Je n'y arriverai jamais.", defigee: "Je n'y arrive pas, pas encore." },
  { figee: "Je suis bloquée.", defigee: "Je suis bloquée, pour l'instant." },
  { figee: "Je suis trop sensible.", defigee: "Je réagis très fort à certaines choses, pour le moment." },
  { figee: "Je suis faible.", defigee: "Je suis fatiguée et fragilisée, pour le moment." },
  { figee: "Je suis incapable de dire non.", defigee: "Dire non est difficile pour moi, pour l'instant." },
  { figee: "Je suis toujours comme ça.", defigee: "J'ai souvent fonctionné comme ça, jusqu'ici." },
  { figee: "Je rate tout.", defigee: "J'ai raté certaines choses, jusqu'ici." },
  { figee: "Je suis dépendante.", defigee: "Cet automatisme me dépasse, pour le moment." },
  { figee: "Je suis grosse.", defigee: "Je ne me sens pas bien dans mon corps, pour le moment." }
];

export default function PourLeMomentPage() {
  const [view, setView] = useState("intro"); // intro, exercise, quick, examples
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    phrase_figee: "",
    ce_qui_fige: [],
    ce_qui_fige_autre: "",
    phrase_defigee: "",
    formule: "",
    porte: "",
    porte_autre: "",
    phrase_finale: ""
  });
  const [quickInput, setQuickInput] = useState("");
  const [quickResult, setQuickResult] = useState("");
  const [quickAction, setQuickAction] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateFinale = (defigee, formule, porte) => {
    const f = FORMULES.find(f => f.id === formule);
    const porteText = porte === "Autre" ? data.porte_autre : porte;
    if (!defigee || !f || !porteText) return "";
    const cleanDefigee = defigee.replace(/\.+$/, "");
    return `${cleanDefigee}. Ma petite porte possible : ${porteText.toLowerCase()}.`;
  };

  const handleStep5 = (porte) => {
    const newData = { ...data, porte };
    const finale = generateFinale(newData.phrase_defigee, newData.formule, porte);
    setData({ ...newData, phrase_finale: finale });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/exercises/pour-le-moment/save`, {
        phrase_figee: data.phrase_figee,
        ce_qui_fige: data.ce_qui_fige.includes("Autre") ? [...data.ce_qui_fige.filter(c => c !== "Autre"), data.ce_qui_fige_autre] : data.ce_qui_fige,
        phrase_defigee: data.phrase_defigee,
        formule: FORMULES.find(f => f.id === data.formule)?.label || "",
        porte: data.porte === "Autre" ? data.porte_autre : data.porte,
        phrase_finale: data.phrase_finale
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(data.phrase_finale);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setStep(1);
    setData({ phrase_figee: "", ce_qui_fige: [], ce_qui_fige_autre: "", phrase_defigee: "", formule: "", porte: "", porte_autre: "", phrase_finale: "" });
    setSaved(false);
  };

  const handleQuickGenerate = () => {
    if (!quickInput.trim()) return;
    let base = quickInput.trim();
    if (base.toLowerCase().startsWith("je suis ")) {
      const adj = base.substring(8).replace(/\.$/, "");
      setQuickResult(`Je me sens ${adj}, pour le moment.`);
    } else {
      if (base.endsWith(".")) base = base.slice(0, -1);
      setQuickResult(`${base}, pour le moment.`);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/dashboard" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200">
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour
        </Link>

        <AnimatePresence mode="wait">
          {/* INTRO */}
          {view === "intro" && (
            <motion.div key="intro" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <IntroView onStart={() => setView("exercise")} onQuick={() => setView("quick")} onExamples={() => setView("examples")} />
            </motion.div>
          )}

          {/* EXERCISE */}
          {view === "exercise" && (
            <motion.div key="exercise" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <ExerciseView
                step={step} setStep={setStep} data={data} setData={setData}
                handleStep5={handleStep5} handleSave={handleSave} handleCopy={handleCopy}
                handleReset={handleReset} saving={saving} saved={saved} copied={copied}
                onBack={() => { setView("intro"); handleReset(); }}
              />
            </motion.div>
          )}

          {/* QUICK MODE */}
          {view === "quick" && (
            <motion.div key="quick" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <QuickView
                input={quickInput} setInput={setQuickInput}
                result={quickResult} action={quickAction} setAction={setQuickAction}
                onGenerate={handleQuickGenerate} onBack={() => { setView("intro"); setQuickInput(""); setQuickResult(""); setQuickAction(""); }}
              />
            </motion.div>
          )}

          {/* EXAMPLES */}
          {view === "examples" && (
            <motion.div key="examples" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <ExamplesView onBack={() => setView("intro")} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

/* ============== INTRO ============== */
function IntroView({ onStart, onQuick, onExamples }) {
  return (
    <div>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-wider text-[#6B8576] font-medium mb-2" style={{ fontFamily: 'Manrope' }}>Soutien émotionnel</p>
        <h1 className="text-3xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="plm-title">Pour le moment</h1>
        <p className="text-[#6A7771] text-sm">Défiger un problème qui commence à ressembler à une identité.</p>
      </div>

      <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8 mb-6" style={{ fontFamily: 'Manrope' }}>
        <p className="text-[#2C3531] leading-relaxed mb-4">
          Quand un problème dure, il peut finir par ressembler à une identité.
        </p>
        <p className="text-[#2C3531] leading-relaxed mb-4">
          On ne dit plus :<br />
          <span className="italic text-[#6A7771]">"Je traverse une difficulté."</span>
        </p>
        <p className="text-[#2C3531] leading-relaxed mb-4">
          On finit par dire :<br />
          <span className="italic text-[#6A7771]">"Je suis comme ça."</span>
        </p>
        <p className="text-[#2C3531] leading-relaxed mb-4">
          Et plus le problème devient une définition de soi, plus il semble solide, permanent, impossible à déplacer.
        </p>
        <p className="text-[#2C3531] leading-relaxed mb-4">
          Cet outil vous aide à ajouter une petite ouverture à la fin d'une phrase trop figée :
        </p>
        <ul className="text-[#2C3531] leading-relaxed mb-4 space-y-1 pl-4">
          <li className="text-sm">- pour le moment</li>
          <li className="text-sm">- pour l'instant</li>
          <li className="text-sm">- pas encore</li>
          <li className="text-sm">- jusqu'ici</li>
          <li className="text-sm">- actuellement</li>
        </ul>
        <p className="text-[#2C3531] leading-relaxed mb-4">
          Ces mots ne nient pas le problème.<br />
          Ils rappellent simplement qu'un état n'est pas forcément une identité.
        </p>
      </div>

      <div className="bg-[#2A3A31] text-white rounded-3xl p-6 mb-6">
        <p className="text-white/90 leading-relaxed text-center" style={{ fontFamily: 'Outfit' }}>
          "Je ne suis pas mon problème. Je le traverse, pour le moment."
        </p>
      </div>

      <div className="space-y-3">
        <button onClick={onStart} className="w-full py-4 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 text-sm" data-testid="plm-start">
          Défiger une phrase
        </button>
        <div className="flex gap-3">
          <button onClick={onQuick} className="flex-1 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium hover:bg-[#FAF9F6] transition-all duration-300 text-sm flex items-center justify-center gap-2" data-testid="plm-quick">
            <Zap className="w-4 h-4" strokeWidth={1.5} /> Version rapide
          </button>
          <button onClick={onExamples} className="flex-1 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium hover:bg-[#FAF9F6] transition-all duration-300 text-sm flex items-center justify-center gap-2" data-testid="plm-examples">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Exemples
          </button>
        </div>
      </div>

      {/* Encadré vigilance */}
      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="plm-safety">
        <h4 className="text-sm font-semibold text-amber-800 mb-2">Attention</h4>
        <p className="text-sm text-amber-700 leading-relaxed mb-2">
          Cet outil ne sert pas à minimiser une souffrance réelle. Dire "pour le moment" ne veut pas dire "ce n'est pas grave". Cela veut simplement dire : "Ce n'est pas toute mon identité, et ce n'est pas forcément définitif."
        </p>
        <p className="text-sm text-amber-700 leading-relaxed">
          Si vous êtes en danger, si vous avez des idées suicidaires, si vous traversez une dépression sévère, une situation de violence ou une détresse importante, cet outil ne suffit pas. Demandez de l'aide à un proche, à un professionnel ou aux services d'urgence.
        </p>
      </div>
    </div>
  );
}

/* ============== EXERCISE 5 STEPS ============== */
function ExerciseView({ step, setStep, data, setData, handleStep5, handleSave, handleCopy, handleReset, saving, saved, copied, onBack }) {
  const progress = (step / 5) * 100;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200" data-testid="plm-back-intro">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour à l'introduction
      </button>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-[#6A7771] font-medium">Étape {step} / 5</p>
          <p className="text-xs text-[#6A7771]">{Math.round(progress)}%</p>
        </div>
        <div className="w-full h-1.5 bg-[#EBE7E0] rounded-full overflow-hidden">
          <motion.div className="h-full bg-[#6B8576] rounded-full" initial={false} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && <Step1 key="s1" data={data} setData={setData} onNext={() => setStep(2)} />}
        {step === 2 && <Step2 key="s2" data={data} setData={setData} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
        {step === 3 && <Step3 key="s3" data={data} setData={setData} onNext={() => setStep(4)} onBack={() => setStep(2)} />}
        {step === 4 && <Step4 key="s4" data={data} setData={setData} onNext={() => setStep(5)} onBack={() => setStep(3)} />}
        {step === 5 && <Step5 key="s5" data={data} setData={setData} handleStep5={handleStep5} handleSave={handleSave} handleCopy={handleCopy} handleReset={handleReset} saving={saving} saved={saved} copied={copied} onBack={() => setStep(4)} />}
      </AnimatePresence>
    </div>
  );
}

/* Step 1 */
function Step1({ data, setData, onNext }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
      <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>La phrase qui fige</h2>
        <p className="text-sm text-[#2C3531] mb-6">Quelle phrase dure vous dites-vous sur vous-même ?</p>
        <textarea
          className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm min-h-[100px] resize-none"
          placeholder="Exemple : Je suis nulle."
          value={data.phrase_figee}
          onChange={e => setData({ ...data, phrase_figee: e.target.value })}
          data-testid="plm-step1-input"
        />
        <p className="text-xs text-[#6A7771] mt-3 leading-relaxed">
          Écrivez la phrase telle qu'elle vient, même si elle est dure, injuste ou excessive. Elle servira de point de départ.
        </p>
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={onNext} disabled={!data.phrase_figee.trim()} className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-40 flex items-center gap-2" data-testid="plm-next-1">
          Suivant <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </motion.div>
  );
}

/* Step 2 */
function Step2({ data, setData, onNext, onBack }) {
  const toggle = (item) => {
    const current = data.ce_qui_fige;
    if (current.includes(item)) setData({ ...data, ce_qui_fige: current.filter(c => c !== item) });
    else setData({ ...data, ce_qui_fige: [...current, item] });
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
      <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>Ce que cette phrase fige</h2>
        <p className="text-sm text-[#2C3531] mb-6">Dans cette phrase, qu'est-ce qui semble définitif ?</p>
        <div className="flex flex-wrap gap-2">
          {FIGE_CHOICES.map(choice => (
            <button key={choice} onClick={() => toggle(choice)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${data.ce_qui_fige.includes(choice) ? 'bg-[#6B8576] text-white' : 'bg-[#FAF9F6] border border-[#EBE7E0] text-[#2C3531] hover:border-[#6B8576]/30'}`}
              data-testid={`plm-fige-${choice.toLowerCase().replace(/\s/g, '-')}`}
            >
              {choice}
            </button>
          ))}
        </div>
        {data.ce_qui_fige.includes("Autre") && (
          <input type="text" className="mt-4 w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm" placeholder="Précisez..." value={data.ce_qui_fige_autre} onChange={e => setData({ ...data, ce_qui_fige_autre: e.target.value })} data-testid="plm-fige-autre-input" />
        )}
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="px-6 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium text-sm hover:bg-[#FAF9F6] transition-all duration-300 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Précédent
        </button>
        <button onClick={onNext} disabled={data.ce_qui_fige.length === 0} className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-40 flex items-center gap-2" data-testid="plm-next-2">
          Suivant <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </motion.div>
  );
}

/* Step 3 */
function Step3({ data, setData, onNext, onBack }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
      <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>Transformer la phrase</h2>
        <p className="text-sm text-[#2C3531] mb-4">Comment pouvez-vous dire la même chose sans en faire toute votre identité ?</p>

        <div className="bg-[#FAF9F6] rounded-2xl p-4 mb-5 border border-[#EBE7E0]/50">
          <p className="text-xs font-medium text-[#6A7771] mb-2">Essayez de transformer :</p>
          <p className="text-sm text-[#2C3531] mb-2 italic">"Je suis..." en :</p>
          <ul className="text-sm text-[#2C3531] space-y-1 pl-2">
            <li>"Je me sens..."</li>
            <li>"Je traverse..."</li>
            <li>"Je souffre de..."</li>
            <li>"J'ai du mal à..."</li>
            <li>"Je n'ai pas encore trouvé comment..."</li>
            <li>"Cette situation me met en difficulté..."</li>
          </ul>
          <p className="text-xs text-[#6A7771] mt-3">Puis ajoutez une ouverture temporelle EN FIN DE PHRASE : pour le moment, pour l'instant, pas encore, jusqu'ici, actuellement.</p>
        </div>

        <div className="bg-[#F0EBE3] rounded-2xl p-3 mb-4 border border-[#E4D1C3]/50">
          <p className="text-xs text-[#6A7771]">Votre phrase de départ :</p>
          <p className="text-sm text-[#2C3531] font-medium italic">"{data.phrase_figee}"</p>
        </div>

        <textarea
          className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-white focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm min-h-[80px] resize-none"
          placeholder="Exemple : Je me sens nulle, pour le moment."
          value={data.phrase_defigee}
          onChange={e => setData({ ...data, phrase_defigee: e.target.value })}
          data-testid="plm-step3-input"
        />
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="px-6 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium text-sm hover:bg-[#FAF9F6] transition-all duration-300 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Précédent
        </button>
        <button onClick={onNext} disabled={!data.phrase_defigee.trim()} className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-40 flex items-center gap-2" data-testid="plm-next-3">
          Suivant <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </motion.div>
  );
}

/* Step 4 */
function Step4({ data, setData, onNext, onBack }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
      <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>Choisir la meilleure formule de fin</h2>
        <p className="text-sm text-[#2C3531] mb-6">Quelle formule ouvre le mieux une porte dans votre phrase ?</p>
        <div className="space-y-3">
          {FORMULES.map(f => (
            <button key={f.id} onClick={() => setData({ ...data, formule: f.id })}
              className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 ${data.formule === f.id ? 'border-[#6B8576] bg-[#6B8576]/5 shadow-sm' : 'border-[#EBE7E0] bg-[#FAF9F6] hover:border-[#6B8576]/30'}`}
              data-testid={`plm-formule-${f.id}`}
            >
              <p className={`text-sm font-medium ${data.formule === f.id ? 'text-[#6B8576]' : 'text-[#2C3531]'}`} style={{ fontFamily: 'Outfit' }}>{f.label}</p>
              <p className="text-xs text-[#6A7771] mt-1">{f.help}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="px-6 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium text-sm hover:bg-[#FAF9F6] transition-all duration-300 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Précédent
        </button>
        <button onClick={onNext} disabled={!data.formule} className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-40 flex items-center gap-2" data-testid="plm-next-4">
          Suivant <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </motion.div>
  );
}

/* Step 5 */
function Step5({ data, setData, handleStep5, handleSave, handleCopy, handleReset, saving, saved, copied, onBack }) {
  const [showPorteAutre, setShowPorteAutre] = useState(false);

  const selectPorte = (p) => {
    if (p === "Autre") {
      setShowPorteAutre(true);
      setData({ ...data, porte: "Autre" });
    } else {
      setShowPorteAutre(false);
      handleStep5(p);
    }
  };

  const confirmAutre = () => {
    if (data.porte_autre.trim()) {
      const cleanDefigee = data.phrase_defigee.replace(/\.+$/, "");
      const finale = `${cleanDefigee}. Ma petite porte possible : ${data.porte_autre.toLowerCase()}.`;
      setData({ ...data, phrase_finale: finale });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
      {!data.phrase_finale ? (
        <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>La petite porte de sortie</h2>
          <p className="text-sm text-[#2C3531] mb-6">Si cette phrase n'était pas définitive, quelle petite porte pourrait s'ouvrir ?</p>
          <div className="flex flex-wrap gap-2">
            {PORTES.map(p => (
              <button key={p} onClick={() => selectPorte(p)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${data.porte === p ? 'bg-[#6B8576] text-white' : 'bg-[#FAF9F6] border border-[#EBE7E0] text-[#2C3531] hover:border-[#6B8576]/30'}`}
                data-testid={`plm-porte-${p.toLowerCase().replace(/\s/g, '-')}`}
              >
                {p}
              </button>
            ))}
          </div>
          {showPorteAutre && (
            <div className="mt-4 flex gap-2">
              <input type="text" className="flex-1 px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm" placeholder="Votre petite porte..." value={data.porte_autre} onChange={e => setData({ ...data, porte_autre: e.target.value })} data-testid="plm-porte-autre-input" />
              <button onClick={confirmAutre} disabled={!data.porte_autre.trim()} className="px-5 py-3 bg-[#6B8576] text-white rounded-2xl font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-40">OK</button>
            </div>
          )}
          <div className="mt-6 flex justify-start">
            <button onClick={onBack} className="px-6 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium text-sm hover:bg-[#FAF9F6] transition-all duration-300 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Précédent
            </button>
          </div>
        </div>
      ) : (
        /* RESULT */
        <div>
          <div className="bg-[#2A3A31] text-white rounded-3xl p-6 sm:p-8 mb-6">
            <p className="text-xs uppercase tracking-wider text-white/50 font-medium mb-3" style={{ fontFamily: 'Manrope' }}>Votre phrase défigée</p>
            <textarea
              className="w-full bg-transparent text-white/90 text-lg leading-relaxed resize-none focus:outline-none min-h-[80px]"
              style={{ fontFamily: 'Outfit' }}
              value={data.phrase_finale}
              onChange={e => setData({ ...data, phrase_finale: e.target.value })}
              data-testid="plm-finale-edit"
            />
          </div>

          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={handleCopy} className="flex-1 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium text-sm hover:bg-[#FAF9F6] transition-all duration-300 flex items-center justify-center gap-2" data-testid="plm-copy">
                {copied ? <><Check className="w-4 h-4" strokeWidth={2} /> Copié</> : <><Copy className="w-4 h-4" strokeWidth={1.5} /> Copier ma phrase</>}
              </button>
              <button onClick={handleSave} disabled={saving || saved} className="flex-1 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="plm-save">
                {saved ? <><Check className="w-4 h-4" strokeWidth={2} /> Enregistré</> : <><Save className="w-4 h-4" strokeWidth={1.5} /> Enregistrer dans mon carnet</>}
              </button>
            </div>
            <button onClick={handleReset} className="w-full py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium text-sm hover:bg-[#FAF9F6] transition-all duration-300 flex items-center justify-center gap-2" data-testid="plm-restart">
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Recommencer avec une autre phrase
            </button>
            <Link to="/dashboard" className="block w-full py-3 text-[#6A7771] hover:text-[#2C3531] transition-colors duration-200 text-sm text-center font-medium" data-testid="plm-quit">
              Quitter l'outil
            </Link>
          </div>

          <div className="mt-8 bg-[#F0EBE3] rounded-3xl p-6 border border-[#E4D1C3]/50">
            <p className="text-[#2C3531] text-sm leading-relaxed text-center" style={{ fontFamily: 'Outfit' }}>
              "Le problème existe. Mais il n'est pas obligé de devenir votre identité, pour toujours."
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

/* ============== QUICK MODE ============== */
function QuickView({ input, setInput, result, action, setAction, onGenerate, onBack }) {
  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200" data-testid="plm-quick-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour
      </button>

      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-5 h-5 text-[#D9A892]" strokeWidth={1.5} />
          <h2 className="text-2xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Version rapide</h2>
        </div>
        <p className="text-sm text-[#6A7771]">Écrivez une phrase commençant par "Je suis..." et obtenez une version défigée.</p>
      </div>

      <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8 space-y-4">
        <div>
          <label className="text-sm font-medium text-[#2C3531] mb-2 block">Votre phrase :</label>
          <input type="text" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm" placeholder="Je suis nulle." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') onGenerate(); }} data-testid="plm-quick-input" />
        </div>
        <button onClick={onGenerate} disabled={!input.trim()} className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-40" data-testid="plm-quick-generate">
          Défiger
        </button>

        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="bg-[#2A3A31] text-white rounded-2xl p-5">
              <p className="text-xs uppercase tracking-wider text-white/50 font-medium mb-2" style={{ fontFamily: 'Manrope' }}>Version défigée</p>
              <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'Outfit' }} data-testid="plm-quick-result">"{result}"</p>
            </div>

            <div>
              <p className="text-sm text-[#2C3531] mb-2 font-medium">Que faudrait-il pour que cette phrase devienne un peu moins vraie, même de 1 % ?</p>
              <input type="text" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm" placeholder="Ma petite action possible :" value={action} onChange={e => setAction(e.target.value)} data-testid="plm-quick-action" />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

/* ============== EXAMPLES ============== */
function ExamplesView({ onBack }) {
  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200" data-testid="plm-examples-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour
      </button>

      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-5 h-5 text-[#D9A892]" strokeWidth={1.5} />
          <h2 className="text-2xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Exemples</h2>
        </div>
        <p className="text-sm text-[#6A7771]">Des phrases figées transformées en phrases ouvertes.</p>
      </div>

      <div className="space-y-3">
        {EXEMPLES.map((ex, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            className="bg-white rounded-2xl border border-[#EBE7E0] p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#D9A892]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#D9A892] text-[10px] font-semibold">{i + 1}</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#6A7771] line-through">{ex.figee}</p>
                <p className="text-sm text-[#2C3531] font-medium mt-1" style={{ fontFamily: 'Outfit' }}>{ex.defigee}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
